import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react'
import {
  GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut as fbSignOut,
  type User,
} from 'firebase/auth'
import { doc, getDoc, serverTimestamp } from 'firebase/firestore'
import { onSnapshot, setDoc } from '@/lib/metered'
import { auth, db, googleProvider } from './firebase'
import { clearIdentity, identify, initAnalytics } from './analytics'
import { sharedCollections } from './sharedCollection'
import {
  CLIENT_DOMAIN, DEFAULT_ROLE_FOR_DOMAIN, MILESTONE_DOMAIN, SEED_ROLE_CAPABILITIES,
  isMilestoneEmail, type Capability, type RoleId,
} from './capabilities'

export type Profile = {
  email: string
  displayName: string | null
  photoURL: string | null
  role: RoleId
  disabled?: boolean
}

type AuthState = {
  user: User | null
  profile: Profile | null
  role: RoleId | null
  isAdmin: boolean
  can: (cap: Capability) => boolean
  loading: boolean
  /** Signed in with Google, but not from an allowed domain. */
  rejectedDomain: string | null
  /** Anything that went wrong during the sign-in round-trip. */
  signInError: string | null
  signIn: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

const domainOf = (email: string | null | undefined) =>
  (email ?? '').toLowerCase().split('@')[1] ?? ''

const messageFor = (err: unknown): string => {
  const code = (err as { code?: string })?.code ?? ''
  if (code === 'auth/operation-not-allowed')
    return 'Google sign-in is not enabled on this Firebase project yet.'
  if (code === 'auth/unauthorized-domain')
    return 'This domain is not on the Firebase authorised list.'
  if (code === 'auth/network-request-failed')
    return 'Network problem reaching Google. Check your connection and try again.'
  if (code === 'auth/popup-blocked')
    return 'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.'
  return err instanceof Error ? err.message : 'Sign-in failed.'
}

const domainAllowed = (email: string | null | undefined) => {
  const d = domainOf(email)
  return d === MILESTONE_DOMAIN || d === CLIENT_DOMAIN
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [caps, setCaps] = useState<Record<string, boolean> | null>(null)
  const [loading, setLoading] = useState(true)
  const [rejectedDomain, setRejectedDomain] = useState<string | null>(null)
  const [signInError, setSignInError] = useState<string | null>(null)

  useEffect(() => { void initAnalytics() }, [])

  // Session
  useEffect(
    () =>
      onAuthStateChanged(auth, async (next) => {
        if (!next) {
          setUser(null); setProfile(null); setCaps(null); setLoading(false)
          return
        }
        if (!domainAllowed(next.email)) {
          // Rejected here for a clear message; the rules reject it again server-side.
          setRejectedDomain(domainOf(next.email) || 'that account')
          await fbSignOut(auth)
          setUser(null); setLoading(false)
          return
        }
        setRejectedDomain(null)
        setUser(next)

        // Self-registration on first sign-in. The rules pin the role to the domain
        // default, so this cannot be used to mint an admin.
        const ref = doc(db, 'users', next.uid)
        const snap = await getDoc(ref)
        if (!snap.exists()) {
          await setDoc(ref, {
            email: (next.email ?? '').toLowerCase(),
            displayName: next.displayName,
            photoURL: next.photoURL,
            role: DEFAULT_ROLE_FOR_DOMAIN(next.email ?? ''),
            disabled: false,
            createdAt: serverTimestamp(),
            lastSeenAt: serverTimestamp(),
          })
        } else {
          await setDoc(ref, { lastSeenAt: serverTimestamp() }, { merge: true })
        }
      }),
    [],
  )

  // Profile — live, so a role change takes effect without a re-login. Signing out clears
  // it rather than leaving it behind: the next sign-in would otherwise render one frame
  // with the previous person's role and capabilities before the listener catches up.
  useEffect(() => {
    if (!user) { setProfile(null); return }
    return onSnapshot(doc(db, 'users', user.uid), (snap) => {
      setProfile(snap.exists() ? (snap.data() as Profile) : null)
      setLoading(false)
    })
  }, [user])

  const role = profile?.role ?? null

  // Capability matrix for this role — also live, so an admin toggling a capability
  // updates every open session.
  useEffect(() => {
    if (!role) { setCaps(null); return }
    return onSnapshot(
      doc(db, 'roles', role),
      (snap) => setCaps((snap.data()?.capabilities as Record<string, boolean>) ?? null),
      // If the roles doc is unreadable, fall back to the seed rather than locking the UI.
      () => setCaps(SEED_ROLE_CAPABILITIES[role] ?? null),
    )
  }, [role])

  const isAdmin = isMilestoneEmail(user?.email)

  // Role and organisation only — never the address. See src/lib/analytics.ts.
  useEffect(() => {
    if (user && role) identify(user.uid, role, isAdmin ? 'milestone' : 'client')
  }, [user, role, isAdmin])

  const can = useCallback(
    (cap: Capability) => {
      if (profile?.disabled) return false
      if (isAdmin) return true
      const live = caps ?? (role ? SEED_ROLE_CAPABILITIES[role] : null)
      return live?.[cap] === true
    },
    [caps, isAdmin, profile?.disabled, role],
  )

  const signIn = useCallback(async () => {
    setRejectedDomain(null)
    setSignInError(null)
    try {
      await signInWithPopup(auth, googleProvider)
    } catch (err) {
      const code = (err as { code?: string })?.code
      // Closing the popup is the user changing their mind, not an error to shout about.
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return
      setSignInError(messageFor(err))
    }
  }, [])

  // The shared collection listeners outlive the components that asked for them, so they
  // have to be stopped by hand at sign-out — otherwise the next person to sign in on this
  // machine renders the previous one's rows for a frame, and the listeners keep running
  // against a session that is gone.
  const signOut = useCallback(async () => {
    clearIdentity()
    sharedCollections.reset()
    await fbSignOut(auth)
  }, [])

  const value = useMemo<AuthState>(
    () => ({ user, profile, role, isAdmin, can, loading, rejectedDomain, signInError, signIn, signOut }),
    [user, profile, role, isAdmin, can, loading, rejectedDomain, signInError, signIn, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}

/**
 * The same state, or null where there is no provider.
 *
 * `useAuth` throwing is right for a page or a drawer: one of those rendered outside the
 * provider is a wiring mistake, and the error says so at the point it happened. It is
 * wrong for a control that merely *offers* something capability-gated — the comment box
 * looks items up, and a comment box should not be the reason a component tree has to
 * carry authentication. Here, no provider means no capabilities, which means the lookup
 * quietly offers nothing.
 */
export function useMaybeAuth(): AuthState | null {
  return useContext(AuthContext)
}

export { GoogleAuthProvider }
